import { useCallback, useRef, useState } from 'react';
import { Content, Item, Portal, Root, Trigger } from '@radix-ui/react-dropdown-menu';
import { Link } from 'react-router-dom';

import {
  buttonClasses,
  containerClasses,
  contentClasses,
  imageClasses,
  itemClasses,
  itemContainerClasses,
  itemRightPaddingClass,
  itemVerticalPaddingClass,
  placeholderClasses
} from 'tailwind/components/StoreSelector/StoreSelector.classes';
import { isDesktop } from 'helpers/ClientUtils';
import type StoreSelectorProps from 'tailwind/components/StoreSelector/StoreSelector.types';
import UtilityStrokeCaretDownMediumIcon from 'tailwind/components/Icons/UtilityStrokeCaretDownMediumIcon';
import UtilityStrokeCaretUpMediumIcon from 'tailwind/components/Icons/UtilityStrokeCaretUpMediumIcon';

const iconSize = 24;
const itemHorizontalPadding = Number(itemRightPaddingClass.match(/\d+/)![0]);
const itemVerticalPadding = Number(itemVerticalPaddingClass.match(/\d+/)![0]);
const mobileLogoHeight = 32; // Height of logo on mobile is set to 32px in .hfImagesComponent

const StoreSelector = ({ height = 40, onOpenChange, stores }: StoreSelectorProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownContainerRef = useRef<HTMLDivElement>(null);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      setIsOpen(open);
      onOpenChange?.();
    },
    [onOpenChange]
  );

  if (!Array.isArray(stores) || stores.length < 1) {
    return null;
  }

  const horizontalOffset = -1 * ((itemHorizontalPadding + iconSize) / 2); // Position menu so button in center of right padding
  const verticalOffset = -1 * (((isDesktop() ? height : mobileLogoHeight) - iconSize) / 2 + itemVerticalPadding); // From icon bottom
  const currentStore = stores[0];

  return (
    <div className={containerClasses} ref={dropdownContainerRef} data-slot-id="store-selector">
      <Link
        aria-label={currentStore.label}
        data-test-id={currentStore.testId ?? 'storeSelectorLogo'}
        onClick={currentStore.onLogoClick}
        to={currentStore.href}
      >
        <img alt={currentStore.logoLabel} height={height} src={currentStore.imageSrc} />
      </Link>

      {stores.length > 1 ? (
        <div className="ml-4">
          <Root onOpenChange={handleOpenChange}>
            <Trigger asChild>
              <button aria-label="Select store" className={buttonClasses} type="button">
                {isOpen ? <UtilityStrokeCaretUpMediumIcon size={iconSize} /> : <UtilityStrokeCaretDownMediumIcon size={iconSize} />}
              </button>
            </Trigger>

            <Portal container={dropdownContainerRef.current}>
              <Content
                align="start"
                alignOffset={verticalOffset}
                avoidCollisions={false}
                className={contentClasses}
                side="left"
                sideOffset={horizontalOffset}
              >
                {stores.map((store, index) =>
                  index === 0 ? (
                    <Item asChild key={store.label}>
                      {
                        // Radix sets role="menuitem", which can be disabled with no href provided
                        // eslint-disable-next-line jsx-a11y/anchor-is-valid
                        <a aria-disabled aria-label={store.label} className={itemClasses}>
                          <img alt={store.label} height={height} src={store.imageSrc} />
                        </a>
                      }
                    </Item>
                  ) : (
                    <Item asChild key={store.label}>
                      <a aria-label={store.label} className={itemClasses} href={store.href}>
                        <div className={itemContainerClasses}>
                          <img alt="" className={placeholderClasses} height={height} src={currentStore.imageSrc} />
                          <img alt={store.label} className={imageClasses} height={height} src={store.imageSrc} />
                        </div>
                      </a>
                    </Item>
                  )
                )}
              </Content>
            </Portal>
          </Root>
        </div>
      ) : null}
    </div>
  );
};

export default StoreSelector;
